// SF23.2: the runtime cutscene player - owns the clock, calls SF23.1's
// sampling functions each frame, applies actor clips, pushes camera targets
// into FirstPersonController's externalCameraOverrideRef, and reports due
// cues/signals/post state via callbacks. Mirrors how sstk-dwindling's
// Being.tsx drives its own game-local cutscene (the-dwindling/cutscenes/
// timeline.ts): a clock the component owns, sampling functions asked "what
// is due this frame", and a clean handback of control on completion or
// interruption - not a hand-rolled per-effect state machine.
//
// Renders nothing of its own; it only drives other things (refs, actor
// instances, callbacks), so it mounts silently inside a Canvas subtree like
// any other non-visual runtime controller.
import { useFrame } from '@react-three/fiber';
import { useEffect, useRef, type MutableRefObject } from 'react';

import {
  activeActorClips,
  activeCameraKey,
  dueAudioCues,
  dueSignals,
  samplePost,
  type CameraKey,
  validateCutscene,
  type Cutscene,
  type PostState,
} from '../content/schema/cutscene';
import { isPrototypeClip, type PrototypeClip } from '../characters/animation/clips';
import type { CameraOverrideTarget } from './FirstPersonController';

/**
 * What the cutscene player needs from an actor to apply the active clip -
 * `PrototypeInstance` (the stock rig) already satisfies this shape exactly;
 * a retargeted rig (SF22.2) needs a small adapter around
 * `retargetClip` + `AnimationMixer.clipAction(...).play()`. Must be a no-op
 * when `clip` is already playing (PrototypeInstance.play's own contract) -
 * the player calls `play` every frame a clip is active, relying on that
 * idempotency rather than tracking "did the clip change" itself.
 */
export interface CutsceneActor {
  play(clip: PrototypeClip, opts?: { fade?: number }): void;
  /**
   * Play a clip id this engine does not know as a built-in prototype clip.
   *
   * `play` is typed to `PrototypeClip`, and `stepCutscene` used to drop any
   * key whose `clipId` failed `isPrototypeClip` before the actor ever saw
   * it. That silently disabled the retargeted-rig path this interface's own
   * doc comment describes, because a retargeted rig's clip ids are exactly
   * the ones that gate rejects. An actor that implements this receives those
   * ids instead of having them thrown away; one that does not is unaffected.
   */
  playRaw?(clipId: string, opts?: { fade?: number }): void;
  /** World-space position and yaw (radians, the convention already used
   *  throughout this engine: forward = [sin(yaw), 0, cos(yaw)]), for a
   *  camera key that tracks this actor (`CameraKey.trackActorId`) instead
   *  of a fixed target. Optional - an actor that never needs to be tracked
   *  can omit it, and a track-mode key falls back to its own authored
   *  approach/lookAt when the named actor doesn't provide one. */
  getWorldTransform?(): { position: [number, number, number]; forwardYaw: number };
}

export interface CutscenePlayerProps {
  cutscene: Cutscene;
  /** Actor instances keyed by the cutscene's own `actorId`s. An actor with
   * no matching key here is simply not driven (its track is ignored, not an
   * error) - lets a cutscene reference an actor the current scene does not
   * happen to have mounted. */
  actors: Record<string, CutsceneActor>;
  /** Written every frame the camera track is `'take'`/`'hold'`, cleared to
   * null on `'release'` and on unmount/completion - pass
   * `FirstPersonController`'s own `externalCameraOverrideRef` here. */
  cameraOverrideRef: MutableRefObject<CameraOverrideTarget | null>;
  /** Called for each audio cue that becomes due this frame. The host
   * resolves what a cue name means (matches Being.tsx's own pattern). */
  onCue?: (cue: string) => void;
  /** Called for each signal that becomes due this frame - the host's own
   * event/Logic system decides what an event name does; this component
   * only reports that it fired, on the frame it was authored to. */
  onSignal?: (event: string) => void;
  /** Called every frame with the timeline's post-effects state; null fields
   * mean "leave the host's own base value alone" (samplePost's contract) -
   * the host applies only the non-null fields. */
  onPost?: (post: PostState) => void;
  /** Called once, the frame the clock reaches the cutscene's duration. */
  onComplete?: () => void;
  /** Freeze the clock without unmounting. */
  paused?: boolean;
}

export interface CutsceneStepCallbacks {
  onCue?: (cue: string) => void;
  onSignal?: (event: string) => void;
  onPost?: (post: PostState) => void;
  onComplete?: () => void;
}

export interface CutsceneStepResult {
  /** The clock's new value, clamped to [0, cutscene.duration]. */
  nextT: number;
  /** True once this step reached (or already was at) the cutscene's duration. */
  completed: boolean;
  /** What the camera override ref should be set to this frame - null means
   * "release" (or no camera key yet), matching `cameraOverrideRef.current`'s
   * own contract directly. */
  cameraOverride: CameraOverrideTarget | null;
}

/**
 * What the camera override ref should become for the given active camera
 * key: a fixed authored target, a live-resolved target for a tracked actor,
 * or null (release, no key yet, or a track key whose actor has neither a
 * live transform nor a fallback approach/lookAt to use).
 */
function resolveCameraOverride(
  cameraKey: CameraKey | null,
  actors: Record<string, CutsceneActor>,
): CameraOverrideTarget | null {
  if (!cameraKey || cameraKey.mode === 'release') return null;

  if (cameraKey.trackActorId) {
    const transform = actors[cameraKey.trackActorId]?.getWorldTransform?.();
    if (transform) {
      const distance = cameraKey.trackDistance ?? 1.1;
      const heightOffset = cameraKey.trackHeightOffset ?? 0;
      const [px, py, pz] = transform.position;
      // Same forward convention as the rest of this engine: yaw 0 faces +Z.
      const forwardX = Math.sin(transform.forwardYaw);
      const forwardZ = Math.cos(transform.forwardYaw);
      return {
        approach: [px + forwardX * distance, py + heightOffset, pz + forwardZ * distance],
        lookAt: [px, py + heightOffset, pz],
        instant: cameraKey.instant,
      };
    }
    // The tracked actor isn't mounted, or doesn't report a transform - fall
    // back to whatever fixed target the key also carries, if any.
  }

  if (cameraKey.approach && cameraKey.lookAt) {
    return { approach: cameraKey.approach, lookAt: cameraKey.lookAt, instant: cameraKey.instant };
  }
  return null;
}

/**
 * One frame of cutscene playback: advances the clock by `dt`, fires due
 * cues/signals/post/actor-clip calls, and resolves what the camera override
 * should become. Pure aside from the callbacks/actors it is explicitly
 * given (no refs, no `useFrame`) - the component below is a thin `useFrame`
 * wrapper around this, kept separate so the actual playback logic is
 * testable without an R3F canvas.
 */
export function stepCutscene(
  cutscene: Cutscene,
  prevT: number,
  dt: number,
  actors: Record<string, CutsceneActor>,
  callbacks: CutsceneStepCallbacks = {},
): CutsceneStepResult {
  const nextT = Math.min(prevT + dt, cutscene.duration);

  for (const cue of dueAudioCues(cutscene, prevT, nextT)) callbacks.onCue?.(cue);
  for (const event of dueSignals(cutscene, prevT, nextT)) callbacks.onSignal?.(event);

  callbacks.onPost?.(samplePost(cutscene, nextT));

  const activeClips = activeActorClips(cutscene, nextT);
  for (const [actorId, clipId] of Object.entries(activeClips)) {
    const actor = actors[actorId];
    if (!actor) continue;
    if (isPrototypeClip(clipId)) actor.play(clipId);
    // Not a built-in clip: hand it to the actor's own resolver if it has
    // one, rather than dropping the key on the floor.
    else actor.playRaw?.(clipId);
  }

  const cameraOverride = resolveCameraOverride(activeCameraKey(cutscene, nextT), actors);

  const completed = nextT >= cutscene.duration;
  if (completed) callbacks.onComplete?.();

  return { nextT, completed, cameraOverride };
}

/**
 * A cheap value signature for a cutscene: its id, duration, and the shape of
 * every track. Two objects with the same signature drive playback
 * identically, so re-deriving the same cutscene during a render does not
 * count as being handed a new one.
 */
function cutsceneSignature(cutscene: Cutscene): string {
  const { camera, actors, post, audio, signals } = cutscene.tracks;
  return [
    cutscene.id,
    cutscene.duration,
    cutscene.interruptible,
    camera.length,
    actors.length,
    post.length,
    audio.length,
    signals.length,
    // Keys are few and small; including their times and targets catches an
    // edit to a track that happens to keep its length the same.
    camera.map((k) => `${k.t}:${k.mode}:${k.trackActorId ?? ''}`).join(','),
    actors.map((k) => `${k.t}:${k.actorId}:${k.clipId}`).join(','),
    post.map((k) => k.t).join(','),
    audio.map((k) => `${k.t}:${k.cue}`).join(','),
    signals.map((k) => `${k.t}:${k.event}`).join(','),
  ].join('|');
}

/**
 * Plays a `Cutscene` start to finish: advances a clock, samples the actor/
 * camera/post/audio/signal tracks each frame via `stepCutscene`, and
 * cleanly hands the camera back (writes `cameraOverrideRef.current = null`)
 * on completion or if the player is unmounted early - control returns
 * whether the cutscene finished or was interrupted, so the host is never
 * left stuck non-interactive.
 */
export function CutscenePlayer({
  cutscene,
  actors,
  cameraOverrideRef,
  onCue,
  onSignal,
  onPost,
  onComplete,
  paused = false,
}: CutscenePlayerProps) {
  const t = useRef(0);
  const completed = useRef(false);

  const cutsceneRef = useRef(cutscene);
  cutsceneRef.current = cutscene;
  const actorsRef = useRef(actors);
  actorsRef.current = actors;
  const callbacksRef = useRef({ onCue, onSignal, onPost, onComplete });
  callbacksRef.current = { onCue, onSignal, onPost, onComplete };

  // Reset the clock when handed a *different* cutscene, and always hand the
  // camera back on unmount - an interrupted cutscene must never leave the
  // player stuck with an override that nothing will ever clear.
  //
  // Keyed on the cutscene's identity-and-shape signature rather than the
  // object reference: a host that builds its cutscene inline, or derives it
  // during render, hands a new object every render, and keying on the
  // reference restarted the clock each time so the cutscene never advanced.
  // `@sstk/intro-kit` hit the same bug in `IntroSequenceChain` and fixed it
  // the same way.
  const signature = cutsceneSignature(cutscene);
  useEffect(() => {
    t.current = 0;
    completed.current = false;
    return () => {
      cameraOverrideRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  // Validate once per distinct cutscene. `validateCutscene` was only ever
  // called by the editor, so anything reaching the runtime by another path
  // skipped every check the schema defines. Reported rather than thrown: a
  // malformed cutscene should not take a shipped game down, but it should
  // not fail silently either.
  useEffect(() => {
    try {
      validateCutscene(cutsceneRef.current);
    } catch (err) {
      console.error('[CutscenePlayer]', err instanceof Error ? err.message : err);
    }
  }, [signature]);

  useFrame((_, dt) => {
    if (paused || completed.current) return;
    const result = stepCutscene(cutsceneRef.current, t.current, dt, actorsRef.current, callbacksRef.current);
    t.current = result.nextT;
    cameraOverrideRef.current = result.cameraOverride;
    if (result.completed) completed.current = true;
  });

  return null;
}
